import { useCallback, useState, type ReactNode } from 'react';
import classNames from 'classnames';

export interface ChartLegendItem {
  id?: string;
  label: string;
  color: string;
  bold?: boolean;
  hidden?: boolean;
  trailing?: ReactNode;
}

export function useLegendHidden() {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const toggle = useCallback((id: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  return { hidden, toggle };
}

export function ChartLegend({
  items,
  onToggle,
}: {
  items: ChartLegendItem[];
  onToggle?: (id: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div
      className="mt-2 d-flex flex-wrap align-items-center justify-content-center gap-3 px-1"
      style={{ minHeight: '1.75rem' }}
    >
      {items.map((item) => {
        const id = item.id ?? item.label;
        const clickable = onToggle != null;
        const muted = Boolean(item.hidden);
        const body = (
          <>
            <span
              className="flex-shrink-0 rounded-1"
              style={{
                width: '0.5rem',
                height: '0.5rem',
                backgroundColor: item.color,
                opacity: muted ? 0.35 : 1,
              }}
            />
            <span>{item.label}</span>
          </>
        );
        return (
          <div key={id} className="d-flex align-items-center gap-2 fs--2">
            {clickable ? (
              <button
                type="button"
                aria-pressed={!muted}
                aria-label={muted ? `نمایش ${item.label}` : `مخفی کردن ${item.label}`}
                onClick={() => onToggle(id)}
                className={classNames(
                  'd-flex align-items-center gap-2 border-0 bg-transparent p-0',
                  muted
                    ? 'text-400 text-decoration-line-through'
                    : item.bold
                      ? 'fw-bold text-1000'
                      : 'text-700',
                )}
              >
                {body}
              </button>
            ) : (
              <div
                className={classNames(
                  'd-flex align-items-center gap-2',
                  item.bold ? 'fw-bold text-1000' : 'text-700',
                )}
              >
                {body}
              </div>
            )}
            {item.trailing}
          </div>
        );
      })}
    </div>
  );
}
